// AffiliateSignup.jsx - The application form, at /affiliate-signup.
//
// Not linked from anywhere. The URL is sent to somebody after a conversation, so
// whoever opens it has already been told what this is and does not need selling
// to again. That is why it opens with the form rather than with an argument.
//
// One page, not four steps. The kit form is stepped because it is filled in by
// somebody who was not planning to fill anything in that afternoon; this is
// filled in by a business owner who expects a form and wants to see how long it
// is before they start. Three groups, one screen, and everything optional except
// the five things we cannot proceed without.
//
// The one question that changes the form is whether they have somewhere
// customers visit. A display stand needs a counter, so somebody without one is
// never asked for an address they would have to make up.
//
// LEGEND
//   1. TYPES / SIZES   the answers offered, so nobody types a category, and the
//                      one answer that has to be typed because it is not on it
//   2. Done            what they see after submitting
//   3. AffiliateSignup the form

import { useState } from "react";

import { Seo } from "../lib/seo.jsx";
import { useAsync, useForm } from "../lib/hooks.js";
import { publicApi } from "../lib/api.js";
import * as analytics from "../lib/analytics.js";
import { partnerTerms, price } from "../lib/partners.js";
import { CONSENT } from "../lib/legal.js";
import { Input, Select, Checkbox, RadioCards, Button, Alert, Panel } from "../ui/kit.jsx";
import { FormAddressField } from "../ui/address.jsx";
import { Reveal } from "../ui/motion.jsx";
import { Check, Shield, ArrowRight } from "../ui/icons.jsx";

////////// 1. THE ANSWERS ON OFFER //////////
// Tiles and lists rather than text boxes wherever the answer is one of a known
// set. It is faster for them and it means the dashboard is reading a category
// rather than forty spellings of "pawn shop".

// Picking this opens a box, and the box is required. What gets sent is what they
// typed in it, never the words "Something else", because the dashboard has to
// read a trade rather than the fact that our list was too short.
const OTHER_TYPE = "Something else";

const TYPES = [
  "Jeweller",
  "Pawn broker",
  "Coin or bullion dealer",
  "Antique dealer",
  "Auctioneer",
  "Estate sale or clearance",
  "Salon, barber or spa",
  "Newsletter, channel or website",
  OTHER_TYPE,
];

const SIZES = ["Under 50", "50 to 200", "200 to 1,000", "Over 1,000", "Not sure"];

////////// 2. AFTERWARDS //////////
// No code and no link here, because nothing works until somebody approves them
// and a link that silently credits nobody is worse than no link at all.

function Done({ name }) {
  return (
    <Panel className="p-7 sm:p-9 text-center max-w-xl mx-auto">
      <Check size={32} className="mx-auto text-positive" />
      <h1 className="display display-md text-ink mt-4">Thank you, {name.split(" ")[0]}.</h1>
      <p className="lede mt-3 text-muted">
        We have your application. A person reads every one of these, usually within one business day.
      </p>
      <p className="hint mt-5 leading-relaxed">
        If we go ahead, you will get an email with your code and your link, and the display goes in
        the post the same week. If we have a question first, we will call the number you gave us.
      </p>
    </Panel>
  );
}

////////// 3. THE FORM //////////

export default function AffiliateSignup() {
  const [done, setDone] = useState(null);
  // What the stand comes with and what a restock costs, from settings. Typed as
  // "twenty" and "two dollars" here until 10 September 2026, which is how this
  // hint and the Work With Us page could have disagreed.
  const { data: settings } = useAsync(() => publicApi.settings(), []);
  const supply = partnerTerms(settings);

  const form = useForm({
    name: "", businessName: "", businessType: "", businessTypeOther: "", email: "", phone: "", website: "",
    hasPremises: null, wantsDisplay: true,
    address: { line1: "", line2: "", city: "", state: "", postal: "", country: "US" },
    customersPerMonth: "", heardAbout: "", payoutMethod: "",
    acceptedTerms: false,
    website_hp: "",
  });

  const submit = form.submit(async (values) => {
    const answer = await publicApi.applyAsAffiliate({
      ...values,
      hasPremises: values.hasPremises === "yes",
      wantsDisplay: values.hasPremises === "yes" && values.wantsDisplay,
      // Sent only when it was asked for, so a link affiliate does not arrive
      // with an empty address block attached to their name.
      address: values.hasPremises === "yes" ? values.address : undefined,
    });
    analytics.trackAffiliateApplied();
    setDone(answer.name || values.name);
    return answer;
  });

  if (done) {
    return (
      <div className="shell band">
        <Seo title="Application received" path="/affiliate-signup" noIndex />
        <Done name={done} />
      </div>
    );
  }

  const premises = form.values.hasPremises === "yes";

  return (
    <div className="shell band">
      {/* Indexed and linked, which it was not to start with. The programme is a
          public avenue now: /work-with-us leads here, and somebody searching for
          a gold buyer to refer customers to is a shop we did not have to go and
          find. The confirmation state above is still noindex, because a thank
          you page in a search result is somebody landing on a receipt for
          something they never did. */}
      <Seo
        title="Refer customers and get paid a share"
        path="/affiliate-signup"
        description="Apply to refer customers to International Gold and Silver. You are paid a share of what every customer you send is paid, and it costs nothing to join."
      />

      <div className="max-w-2xl mx-auto">
        <Reveal>
          <p className="eyebrow">Work with us</p>
          <h1 className="display display-lg text-ink mt-2">Tell us about you.</h1>
          <p className="lede mt-4 measure text-muted">
            This is the whole application. It takes about three minutes, and a person reads every
            one. Nothing here commits you to anything.
          </p>
        </Reveal>

        <form onSubmit={submit} noValidate className="mt-10 space-y-10">
          {/* Nobody legitimate fills this in. */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            className="hidden"
            aria-hidden="true"
            {...form.field("website_hp")}
          />

          <section className="space-y-4">
            <h2 className="eyebrow">Who you are</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Your name" required autoFocus autoComplete="name" {...form.field("name")} />
              <Input label="Business name" hint="Leave it if you are applying as yourself." {...form.field("businessName")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Email" type="email" required autoComplete="email" {...form.field("email")} />
              <Input label="Phone" type="tel" required autoComplete="tel" hint="We call before we set anybody up." {...form.field("phone")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Select label="What you do" placeholder="Choose one" options={TYPES} {...form.field("businessType")} />
              <Input label="Website or page" placeholder="Optional" {...form.field("website")} />
            </div>

            {/* Required, and only asked when the list has failed them. The
                server checks it too, because a required field the browser
                enforces is a required field a script ignores. */}
            {form.values.businessType === OTHER_TYPE && (
              <Input
                label="So what are you?"
                required
                autoFocus
                placeholder="Locksmith, funeral director, scrap yard"
                hint="A word or two is plenty. It is what we will call you on our side."
                className="fade"
                {...form.field("businessTypeOther")}
              />
            )}
          </section>

          <section className="space-y-4">
            <h2 className="eyebrow">Where you are</h2>
            <RadioCards
              label="Do you have a place customers come to?"
              hint="A shop, a counter, a unit, a market stall. This decides whether a display stand is any use to you."
              name="hasPremises"
              options={[
                { value: "yes", label: "Yes, customers come to us", hint: "We can send a display stand and mailers." },
                { value: "no", label: "No, I refer people online", hint: "You get a link, and no stand to find room for." },
              ]}
              value={form.values.hasPremises}
              onChange={(value) => form.set("hasPremises", value)}
              error={form.errors.hasPremises}
            />

            {premises && (
              <div className="space-y-4 fade">
                <FormAddressField form={form} label="Street" hint="Pick yours from the list and we will fill in the rest" />
                <Input label="Unit, suite or floor" {...form.field("address.line2")} />
                <div className="grid grid-cols-3 gap-4">
                  <Input label="City" {...form.field("address.city")} />
                  <Input label="State" maxLength={2} {...form.field("address.state")} />
                  <Input label="ZIP" {...form.field("address.postal")} />
                </div>
                <Checkbox
                  label="Send a display stand"
                  hint={`The stand, ${supply.startingMailers} mailers and the booklets, free. More mailers after that are ${price(supply.mailerPrice)} each.`}
                  checked={form.values.wantsDisplay}
                  onChange={(event) => form.set("wantsDisplay", event.target.checked)}
                />
              </div>
            )}
          </section>

          <section className="space-y-4">
            <h2 className="eyebrow">How it would work</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Select label="People through the door a month" placeholder="Choose one" options={SIZES} {...form.field("customersPerMonth")} />
              <Input label="How you heard about us" placeholder="Optional" {...form.field("heardAbout")} />
            </div>
            {/* Their money, coming from us. The old wording read as though they
                were the ones paying, which is the opposite of the arrangement. */}
            <Input
              label="How we should pay you"
              placeholder="Check, Zelle, ACH"
              hint="How you would like your commission sent. We confirm the details with you before the first payment."
              {...form.field("payoutMethod")}
            />
          </section>

          <section className="space-y-4 pt-2 border-t border-rule">
            <Checkbox
              label={CONSENT.affiliate}
              checked={form.values.acceptedTerms}
              onChange={(event) => form.set("acceptedTerms", event.target.checked)}
              error={form.errors.acceptedTerms}
            />
            <p className="hint leading-relaxed">
              Read them first:{" "}
              <a href="/legal/affiliate-terms" target="_blank" rel="noopener noreferrer" className="link text-ink">
                Affiliate Terms
              </a>{" "}
              and the{" "}
              <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="link text-ink">
                Privacy Policy
              </a>. The short version: we set the rate and we can change it, a change only ever
              applies to orders that come in afterwards, and nothing is owed until a customer you
              sent is actually paid.
            </p>

            {form.message && <Alert tone="error">{form.message.text}</Alert>}

            <Button type="submit" size="lg" loading={form.busy} className="w-full sm:w-auto">
              {form.busy ? "Sending" : "Send my application"} <ArrowRight size={17} />
            </Button>

            <p className="hint flex items-center gap-2 pt-1">
              <Shield size={14} className="text-bronze shrink-0" />
              An application is not an agreement. Nothing starts until we have spoken and somebody
              here approves it.
            </p>
          </section>
        </form>
      </div>
    </div>
  );
}
